import React, { useState } from 'react';
import { api } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { FileText, Link as LinkIcon, ClipboardCheck, ExternalLink, Save, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

/**
 * ShowDetailsCard — visible only when stage === 'show'.
 * Tabbed UI: Trascrizione | Proposta | Report.
 * Edits persist to lead.custom_fields directly.
 */
export default function ShowDetailsCard({ lead, onUpdated }) {
  const cf = lead.custom_fields || {};
  const [tab, setTab] = useState('transcript');
  const [transcript, setTranscript] = useState(cf.show_transcript || '');
  const [proposalUrl, setProposalUrl] = useState(cf.proposta_url || '');
  const [reportNotes, setReportNotes] = useState(cf.show_report_notes || '');
  const [saving, setSaving] = useState(null); // 'transcript' | 'proposal' | 'report' | null

  const save = async (which, payload) => {
    setSaving(which);
    try {
      await api.patch(`/leads/${lead.id}`, payload);
      toast.success('Salvato');
      onUpdated?.();
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Errore salvataggio';
      toast.error(typeof msg === 'string' ? msg : 'Errore salvataggio');
    } finally {
      setSaving(null);
    }
  };

  return (
    <div className="rounded-lg border border-emerald-200 bg-emerald-50/30 p-3" data-testid="show-details-card">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[11px] uppercase tracking-wide text-emerald-800 font-semibold flex items-center gap-1.5">
          <ClipboardCheck className="w-3.5 h-3.5" /> Show — dettagli della call
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="w-full">
          <TabsTrigger value="transcript" className="text-xs" data-testid="show-tab-transcript">
            <FileText className="w-3.5 h-3.5 mr-1" /> Trascrizione
          </TabsTrigger>
          <TabsTrigger value="proposal" className="text-xs" data-testid="show-tab-proposal">
            <LinkIcon className="w-3.5 h-3.5 mr-1" /> Proposta
          </TabsTrigger>
          <TabsTrigger value="report" className="text-xs" data-testid="show-tab-report">
            <ClipboardCheck className="w-3.5 h-3.5 mr-1" /> Report
          </TabsTrigger>
        </TabsList>

        <TabsContent value="transcript" className="mt-2 space-y-2">
          <Textarea
            rows={5}
            placeholder="Trascrizione della call (riassunto, punti chiave, obiezioni…)"
            value={transcript}
            onChange={(e) => setTranscript(e.target.value)}
            data-testid="show-transcript-textarea"
          />
          <Button
            size="sm"
            onClick={() => save('transcript', { custom_fields: { show_transcript: transcript } })}
            disabled={saving === 'transcript'}
            data-testid="save-transcript-btn"
          >
            {saving === 'transcript' ? (
              <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
            ) : (
              <Save className="w-3.5 h-3.5 mr-1" />
            )}
            Salva trascrizione
          </Button>
        </TabsContent>

        <TabsContent value="proposal" className="mt-2 space-y-2">
          <div>
            <Label className="text-xs uppercase tracking-wide">Link proposta</Label>
            <Input
              value={proposalUrl}
              onChange={(e) => setProposalUrl(e.target.value)}
              placeholder="https://drive.google.com/…"
              data-testid="show-proposal-url"
              className="mt-1"
            />
          </div>
          <div className="flex gap-2">
            <Button
              size="sm"
              onClick={() => save('proposal', { custom_fields: { proposta_url: proposalUrl } })}
              disabled={saving === 'proposal'}
              data-testid="save-proposal-btn"
            >
              {saving === 'proposal' ? (
                <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
              ) : (
                <Save className="w-3.5 h-3.5 mr-1" />
              )}
              Salva URL
            </Button>
            {proposalUrl && (
              <Button variant="outline" size="sm" asChild>
                <a href={proposalUrl} target="_blank" rel="noopener noreferrer" data-testid="open-proposal-link">
                  <ExternalLink className="w-3.5 h-3.5 mr-1" /> Apri
                </a>
              </Button>
            )}
          </div>
        </TabsContent>

        <TabsContent value="report" className="mt-2 space-y-2">
          <Textarea
            rows={5}
            placeholder="Note del report Zoom (esito chiamata, prossimi step, obiezioni risolte…)"
            value={reportNotes}
            onChange={(e) => setReportNotes(e.target.value)}
            data-testid="show-report-textarea"
          />
          <Button
            size="sm"
            onClick={() => save('report', { custom_fields: { show_report_notes: reportNotes } })}
            disabled={saving === 'report'}
            data-testid="save-report-btn"
          >
            {saving === 'report' ? (
              <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
            ) : (
              <Save className="w-3.5 h-3.5 mr-1" />
            )}
            Salva note report
          </Button>
          <p className="text-[10px] text-muted-foreground italic">
            Per il report completo Zoom usa il pulsante “Invia report Zoom” nelle azioni del lead.
          </p>
        </TabsContent>
      </Tabs>
    </div>
  );
}
